import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, FileText, ChevronDown, ChevronUp, Paperclip } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi, saasApi } from '../../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import BankAccountSelect from '@/components/common/BankAccountSelect';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';

interface PurchaseInvoiceLedgerModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: string;
}

export default function PurchaseInvoiceLedgerModal({ isOpen, onClose, supplier }: PurchaseInvoiceLedgerModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<any>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [adjustAdvanceAmount, setAdjustAdvanceAmount] = useState<number | ''>('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [bankAccount, setBankAccount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [attachment, setAttachment] = useState<File | null>(null);

  const {
    dateRange, setDateRange,
    customFrom, setCustomFrom,
    customTo, setCustomTo,
    from_date, to_date,
  } = useReportDateRange('This Month');
  const [statusFilter, setStatusFilter] = useState('Unpaid');

  const { data: supplierAdvance } = useQuery({
    queryKey: ['supplierAdvance', supplier],
    queryFn: async () => {
      if (!supplier) return 0;
      const res = await posApi.getSupplierAdvance(supplier);
      return res.data.message?.unallocated_advance || 0;
    },
    enabled: isOpen && !!supplier
  });

  const { data: banks = [] } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message || [];
    },
    enabled: isOpen,
  });

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['purchaseInvoices', supplier, from_date, to_date, statusFilter],
    queryFn: async () => {
      const params: any = { supplier };
      if (from_date) params.from_date = from_date;
      if (to_date) params.to_date = to_date;
      if (statusFilter) params.status = statusFilter;
      const res = await posApi.getPurchaseInvoices(params);
      return res.data.message || [];
    },
    enabled: isOpen && !!supplier,
  });

  const addPaymentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedInvoiceForPayment) return;
      if ((Number(paymentAmount) || 0) > 0 && paymentMethod === 'Bank' && !bankAccount) {
        throw new Error('Select a bank');
      }
      const bank = banks.find((b: { name: string; bank_name: string }) => b.name === bankAccount);
      let fileUrl = '';
      if (attachment) {
        const formData = new FormData();
        formData.append('file', attachment);
        const uploadRes = await saasApi.uploadFile(formData);
        fileUrl = uploadRes.data?.message?.file_url || '';
      }
      await posApi.addPurchaseInvoicePayment(selectedInvoiceForPayment.name, {
        amount: Number(paymentAmount) || 0,
        adjust_advance: Number(adjustAdvanceAmount) || 0,
        attachment: fileUrl,
        payment_method: paymentMethod,
        bank_account: paymentMethod === 'Bank' ? bankAccount : '',
        bank_name: paymentMethod === 'Bank' ? (bank?.bank_name || '') : '',
        account: paymentMethod === 'Bank' ? (bank?.bank_name || '') : '',
        reference_no: paymentRef,
        date: paymentDate
      });
    },
    onSuccess: () => {
      toast.success('Payment added successfully');
      queryClient.invalidateQueries({ queryKey: ['purchaseInvoices', supplier] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setPaymentModalOpen(false);
      setAdjustAdvanceAmount('');
      setPaymentAmount('');
      setPaymentMethod('Cash');
      setBankAccount('');
      setAttachment(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'Failed to add payment');
    }
  });

  if (!isOpen) return null;

  const openPay = (inv: any, balanceDue: number) => {
    setSelectedInvoiceForPayment(inv);
    const availableAdvance = supplierAdvance || 0;
    const advanceToUse = Math.min(availableAdvance, balanceDue);
    setAdjustAdvanceAmount(advanceToUse > 0 ? advanceToUse : '');
    const remainingPayment = Math.max(0, balanceDue - advanceToUse);
    setPaymentAmount(remainingPayment > 0 ? String(remainingPayment) : '');
    setPaymentModalOpen(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Purchase Invoices</h3>
              <p className="text-sm text-gray-500 truncate">Ledger for {supplier}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="px-4 pt-4 pb-2 border-b border-gray-100 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <ReportDateRangeFilter
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
              className="flex-1"
            />
            <div className="w-full sm:w-48">
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                className="input-field py-1.5 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="Unpaid">Unpaid</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : invoices?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-gray-900 font-medium">No purchase invoices found</h3>
              <p className="text-gray-500 text-sm mt-1">This supplier has no purchase history yet.</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              {/* List header — Frappe-style columns */}
              <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_0.9fr_0.7fr_0.9fr_0.9fr_auto] gap-3 px-3 py-2 bg-gray-50 border-b border-gray-100 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                <div>Invoice</div>
                <div>Date</div>
                <div>Status</div>
                <div className="text-right">Net Total</div>
                <div className="text-right">Balance</div>
                <div className="min-w-[100px]" />
              </div>

              <div className="divide-y divide-gray-100">
                {invoices?.map((inv: any) => {
                  const balanceDue = Math.max(0, Number(inv.balance_amount) || 0);
                  const isPaid = inv.status === 'Paid' || balanceDue <= 0;
                  const isExpanded = expandedInvoice === inv.name;

                  return (
                    <div key={inv.name} className={isExpanded ? 'bg-gray-50/40' : 'bg-white'}>
                      <div
                        className="px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedInvoice(isExpanded ? null : inv.name)}
                      >
                        {/* Desktop: full-width columns */}
                        <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_0.9fr_0.7fr_0.9fr_0.9fr_auto] gap-3 items-center">
                          <div className="min-w-0 flex items-center gap-1.5">
                            <span className="font-medium text-gray-900 text-sm truncate">{inv.name}</span>
                            {inv.attachment && (
                              <a
                                href={inv.attachment}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="shrink-0 text-primary-600 hover:text-primary-700"
                                title="View attachment"
                              >
                                <Paperclip className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            {inv.invoice_date ? format(new Date(inv.invoice_date), "dd MMM yyyy") : '-'}
                          </div>
                          <div>
                            <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${isPaid ? 'bg-emerald-100 text-emerald-700' :
                              inv.status === 'Partially Paid' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                              {isPaid ? 'Paid' : inv.status}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900 text-right">
                            {formatMoney(Number(inv.net_total))}
                          </div>
                          <div className={`text-sm font-medium text-right ${balanceDue > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {formatMoney(balanceDue)}
                          </div>
                          <div className="flex items-center justify-end gap-1.5 shrink-0 flex-nowrap min-w-[100px]">
                            {!isPaid && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPay(inv, balanceDue);
                                }}
                                className="shrink-0 px-2 py-1 text-[11px] bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium rounded transition-colors inline-flex items-center gap-0.5 whitespace-nowrap"
                              >
                                <Plus className="w-3 h-3" /> Pay
                              </button>
                            )}
                            <span className="text-gray-400">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </span>
                          </div>
                        </div>

                        {/* Mobile: still compact but spread across width */}
                        <div className="sm:hidden grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 items-center">
                          <div className="min-w-0 flex items-center gap-1.5">
                            <span className="font-medium text-gray-900 text-sm truncate">{inv.name}</span>
                            <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded ${isPaid ? 'bg-emerald-100 text-emerald-700' :
                              inv.status === 'Partially Paid' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                              {isPaid ? 'Paid' : inv.status}
                            </span>
                            {inv.attachment && (
                              <a
                                href={inv.attachment}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="shrink-0 text-primary-600"
                                title="View attachment"
                              >
                                <Paperclip className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 justify-end shrink-0 flex-nowrap">
                            {!isPaid && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPay(inv, balanceDue);
                                }}
                                className="shrink-0 px-2 py-1 text-[11px] bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium rounded transition-colors inline-flex items-center gap-0.5 whitespace-nowrap"
                              >
                                <Plus className="w-3 h-3" /> Pay
                              </button>
                            )}
                            <span className="text-gray-400">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </span>
                          </div>
                          <div className="col-span-2 grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <div className="text-gray-400">Date</div>
                              <div className="text-gray-700">
                                {inv.invoice_date ? format(new Date(inv.invoice_date), "dd MMM yyyy") : '-'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-gray-400">Net Total</div>
                              <div className="font-medium text-gray-900">{formatMoney(Number(inv.net_total))}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-gray-400">Balance</div>
                              <div className={`font-medium ${balanceDue > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {formatMoney(balanceDue)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50 p-3 sm:p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                          {/* Items */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Items Purchased</h4>
                            {/* Desktop table */}
                            <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {inv.items?.map((item: any, idx: number) => (
                                    <tr key={idx}>
                                      <td className="px-3 py-2">
                                        <div className="text-sm font-medium text-gray-900">{item.item_name}</div>
                                        {item.variant && <div className="text-xs text-gray-500">{item.variant}</div>}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-right text-gray-600">{item.quantity}</td>
                                      <td className="px-3 py-2 text-sm text-right text-gray-600">{formatMoney(Number(item.rate))}</td>
                                      <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">{formatMoney(Number(item.amount))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {/* Mobile cards */}
                            <div className="sm:hidden space-y-2">
                              {inv.items?.map((item: any, idx: number) => (
                                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                                  <div className="text-sm font-medium text-gray-900">{item.item_name}</div>
                                  {item.variant && <div className="text-xs text-gray-500 mb-1">{item.variant}</div>}
                                  <div className="flex justify-between text-xs text-gray-600 mt-1">
                                    <span>Qty {item.quantity}</span>
                                    <span>@ {formatMoney(Number(item.rate))}</span>
                                    <span className="font-medium text-gray-900">{formatMoney(Number(item.amount))}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Payments */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Payment History</h4>
                            {inv.payments?.length > 0 ? (
                              <>
                                <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ref</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {inv.payments?.map((payment: any, idx: number) => (
                                        <tr key={idx}>
                                          <td className="px-3 py-2 text-sm text-gray-600">{payment.date ? format(new Date(payment.date), "dd MMM yyyy") : '-'}</td>
                                          <td className="px-3 py-2 text-sm text-gray-600">
                                            {payment.payment_method}
                                            {payment.bank_name ? ` (${payment.bank_name})` : ''}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-gray-600">{payment.reference_no || '-'}</td>
                                          <td className="px-3 py-2 text-sm text-right font-medium text-emerald-600">{formatMoney(Number(payment.amount))}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="sm:hidden space-y-2">
                                  {inv.payments?.map((payment: any, idx: number) => (
                                    <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                                      <div className="flex justify-between items-start gap-2">
                                        <div>
                                          <div className="text-sm font-medium text-gray-900">
                                            {payment.payment_method}
                                            {payment.bank_name ? ` (${payment.bank_name})` : ''}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {payment.date ? format(new Date(payment.date), "dd MMM yyyy") : '-'}
                                            {payment.reference_no ? ` · ${payment.reference_no}` : ''}
                                          </div>
                                        </div>
                                        <div className="text-sm font-medium text-emerald-600 whitespace-nowrap">
                                          {formatMoney(Number(payment.amount))}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div className="text-sm text-gray-500 italic p-3 bg-white border border-gray-200 rounded-lg">
                                No payments recorded for this invoice yet.
                              </div>
                            )}

                            {inv.notes && (
                              <div className="mt-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">Notes</h4>
                                <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border border-gray-200 break-words">{inv.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </div>
      </div>

      {paymentModalOpen && selectedInvoiceForPayment && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-sm max-h-[95vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
              <h4 className="font-semibold text-gray-900">{t('supplierPayment.title')}</h4>
              <button type="button" onClick={() => { setPaymentModalOpen(false); setAttachment(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={e => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={e => {
                    const next = e.target.value;
                    setPaymentMethod(next);
                    if (next === 'Bank' && !bankAccount && banks.length) {
                      const def = banks.find((b: { is_default: number }) => b.is_default) || banks[0];
                      setBankAccount(def.name);
                    }
                    if (next !== 'Bank') setBankAccount('');
                  }}
                  className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg"
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank</option>
                </select>
              </div>
              {paymentMethod === 'Bank' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank *</label>
                  <BankAccountSelect
                    value={bankAccount}
                    onChange={setBankAccount}
                    banks={banks}
                    required
                  />
                </div>
              )}
              {(supplierAdvance || 0) > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <div className="flex justify-between items-center mb-2 gap-2">
                    <h4 className="font-medium text-sm text-blue-900">Advance Available</h4>
                    <span className="text-sm font-semibold text-blue-700">{formatMoney((supplierAdvance || 0))}</span>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">Adjust Advance ({getCurrencySymbol()})</label>
                    <input
                      type="number"
                      min="0"
                      max={Math.min(supplierAdvance || 0, selectedInvoiceForPayment.balance_amount)}
                      value={adjustAdvanceAmount}
                      onChange={e => {
                        const val = Number(e.target.value);
                        const maxAllowed = Math.min(supplierAdvance || 0, selectedInvoiceForPayment.balance_amount);
                        if (val > maxAllowed) setAdjustAdvanceAmount(maxAllowed);
                        else setAdjustAdvanceAmount(e.target.value === '' ? '' : val);
                      }}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount ({getCurrencySymbol()})</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                  max={selectedInvoiceForPayment.balance_amount}
                  className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Balance Due: {formatMoney(Number(selectedInvoiceForPayment.balance_amount))}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference No (Optional)</label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={e => setPaymentRef(e.target.value)}
                  placeholder="Cheque No, Txn ID, etc."
                  className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attachment</label>
                <input
                  type="file"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end shrink-0">
              <button
                type="button"
                onClick={() => { setPaymentModalOpen(false); setAttachment(null); }}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => addPaymentMutation.mutate()}
                disabled={addPaymentMutation.isPending || (!paymentAmount && !adjustAdvanceAmount) || (Number(paymentAmount) <= 0 && Number(adjustAdvanceAmount) <= 0)}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded-lg disabled:opacity-50"
              >
                {addPaymentMutation.isPending ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
